import { useCallback, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import cn from 'classnames';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  declineTicket,
  getTicketCount,
  getEvenTicketList,
  exportTicketsToExcel,
  approveTicket,
  toggleTicketPaid,
  TicketRequestStatus,
} from '@core';
import Card from 'components/Card';
import Form from 'components/Form';
import styles from './TicketRequestList.module.sass';
import Table from './Table';
import Modal from 'components/Modal';
import { IEventTicketListItem } from '@types';
import ApproveTicket from './ApproveTicket';
import Icon from 'components/Icon';
import CreateTicket from './CreateTicket';

export const TicketRequestList: React.FC<{ maxTicketCount: number }> = ({ maxTicketCount }) => {
  const params = useParams();

  const [selectedTicket, setSelectedTicket] = useState<IEventTicketListItem | undefined>();
  const [visibleApproveTicketModal, setVisibleApproveTicketModal] = useState(false);
  const [visibleCreateTicketModal, setVisibleCreateTicketModal] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  const [page, setPage] = useState(1);

  const { data: approvedTicketCount, reload: reloadCount } = useAsync({
    promiseFn: getTicketCount,
    id: params.id,
  });

  const {
    data: usersList,
    isLoading: isUserListLoading,
    reload,
    run,
  } = useAsync({
    deferFn: getEvenTicketList,
  });

  const [search, setSearch] = useState('');

  const handleDecline = useCallback(
    async (ticketId: string) => {
      try {
        const eventId = params.id;
        if (!eventId) {
          return;
        }
        const result = await declineTicket(eventId, ticketId);
        if (result.data) {
          toast.info('ჩანაწერი უარყოფილია');
          reload();
          reloadCount();
          return;
        }
      } catch {
        toast.error('დაფიქსირდა შეცდომა, თავიდან სცადეთ');
      }
    },
    [params.id, reload, reloadCount],
  );

  const handleTogglePaid = useCallback(
    async (ticketId: string, amount?: number) => {
      try {
        await toggleTicketPaid(ticketId, amount);
        reload();
      } catch {
        toast.error('დაფიქსირდა შეცდომა, თავიდან სცადეთ');
      }
    },
    [reload],
  );

  const onApproveSuccess = () => {
    setSelectedTicket(undefined);
    setVisibleApproveTicketModal(false);
    reload();
    reloadCount();
  };

  const onCreateTicketSuccess = () => {
    setVisibleCreateTicketModal(false);
    reload();
    reloadCount();
  };

  const handleApproveClickClick = (item: IEventTicketListItem) => {
    setSelectedTicket(item);
    setVisibleApproveTicketModal(true);
  };

  const handleBulkApprove = useCallback(
    async (ticketIds: string[]) => {
      const confirmed = window.confirm(`ნამდვილად გსურთ ბილეთების დადასტურება?`);
      if (!confirmed) {
        return;
      }

      try {
        const eventId = params.id;
        if (!eventId) {
          return;
        }

        const filteredTicketIds = usersList?.data.items.filter(
          ticket =>
            (ticket.status == TicketRequestStatus.Declined || ticket.status == TicketRequestStatus.Pending) &&
            ticketIds.includes(ticket.id),
        );
        if (!filteredTicketIds || filteredTicketIds.length === 0) {
          return;
        }

        // Process each ticket individually since there's no bulk API
        const promises = filteredTicketIds.map(async ticket => {
          if (ticket) {
            return approveTicket(eventId, { ticketId: ticket.id, quantity: ticket.quantity });
          }
          return Promise.resolve();
        });

        await Promise.all(promises);
        toast.success(`${filteredTicketIds.length} ბილეთი წარმატებით დადასტურდა`);
        reload();
        reloadCount();
        setSelectedTickets([]); // Clear selection after bulk action
      } catch {
        toast.error('დაფიქსირდა შეცდომა, თავიდან სცადეთ');
      }
    },
    [params.id, usersList, reload, reloadCount],
  );

  const handleBulkDecline = useCallback(
    async (ticketIds: string[]) => {
      const confirmed = window.confirm(`ნამდვილად გსურთ ბილეთების უარყოფა?`);
      if (!confirmed) {
        return;
      }

      try {
        const eventId = params.id;
        if (!eventId) {
          return;
        }

        const filteredTicketIds = usersList?.data.items.filter(
          ticket =>
            (ticket.status == TicketRequestStatus.Pending || ticket.status == TicketRequestStatus.Approved) &&
            ticketIds.includes(ticket.id),
        );
        if (!filteredTicketIds || filteredTicketIds.length === 0) {
          return;
        }

        // Process each ticket individually since there's no bulk API
        const promises = filteredTicketIds.map(async ticket => {
          if (ticket) {
            return declineTicket(eventId, ticket.id);
          }
          return Promise.resolve();
        });

        await Promise.all(promises);

        toast.success(`${filteredTicketIds.length} ბილეთი წარმატებით უარყოფილია`);
        reload();
        reloadCount();
        setSelectedTickets([]); // Clear selection after bulk action
      } catch {
        toast.error('დაფიქსირდა შეცდომა, თავიდან სცადეთ');
      }
    },
    [params.id, reload, reloadCount, usersList?.data?.items],
  );

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    const allTicketIds = usersList?.data?.items.map(ticket => ticket.id) || [];
    if (checked) {
      setSelectedTickets(allTicketIds);
    } else {
      setSelectedTickets([]);
    }
  };

  const handleSelectTicket = (ticketId: string, checked: boolean) => {
    if (checked) {
      setSelectedTickets(prev => [...prev, ticketId]);
    } else {
      setSelectedTickets(prev => prev.filter(id => id !== ticketId));
    }
  };

  // Clear selection when page changes
  useEffect(() => {
    setSelectedTickets([]);
  }, [page]);

  useEffect(() => {
    const interval = setInterval(() => {
      reloadCount();
    }, 20000); // 20 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [reloadCount]);

  const eventId = params.id || '';

  const handleExport = () => {
    void exportTicketsToExcel(eventId);
  };

  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      run(params.id, page, search);
    }, 400); // 400ms debounce delay

    // Cleanup timeout if dependencies change before the delay finishes
    return () => clearTimeout(debounceTimeout);
  }, [page, search, run, params.id]);

  return (
    <>
      <Modal
        outerClassName={styles.outer}
        visible={visibleApproveTicketModal}
        onClose={() => setVisibleApproveTicketModal(false)}
      >
        <ApproveTicket ticket={selectedTicket} eventId={eventId} success={onApproveSuccess} />
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={visibleCreateTicketModal}
        onClose={() => setVisibleCreateTicketModal(false)}
      >
        <CreateTicket eventId={eventId} success={onCreateTicketSuccess} />
      </Modal>

      <Card className={styles.card} classTitle={cn('title-purple', styles.title)}>
        <div>
          მაქსიმალური - {maxTicketCount} (დარჩენილი:
          {maxTicketCount - (approvedTicketCount?.data?.total ?? 0)})
        </div>
        <div>მოთხოვნილი ბილეთები - {approvedTicketCount?.data?.total ?? 0}</div>
        <div>დადასტურებული - {approvedTicketCount?.data?.approved ?? 0}</div>
        <div>დაესწრო - {approvedTicketCount?.data?.delivered ?? 0}</div>
        <div>
          არ დაესწრო - {(approvedTicketCount?.data?.approved ?? 0) - (approvedTicketCount?.data?.delivered ?? 0)}
        </div>
        <br />
        <div className={styles.search}>
          <Form
            className={styles.form}
            value={search}
            setValue={handleSearchChange}
            onSubmit={() => {}}
            placeholder="ძებნა"
            type="text"
            name="search"
            icon="search"
          />
          <div className={styles.exportBtn}>
            <div className={cn('button', 'button-small', styles.button, styles.exportButton)} onClick={handleExport}>
              <span>ექსპორტი</span>
            </div>
            <div
              onClick={() => setVisibleCreateTicketModal(true)}
              className={cn('button', 'button-small', styles.button)}
            >
              <Icon name="add" size="24" />
              <span>დამატება</span>
            </div>
          </div>
        </div>
        <div className={cn(styles.row, { [styles.flex]: true })}>
          <Table
            tickets={usersList}
            isRequestLoading={isUserListLoading}
            onDecline={$event => void handleDecline($event)}
            onApprove={item => handleApproveClickClick(item)}
            onTogglePaid={(id, amount) => void handleTogglePaid(id, amount)}
            onBulkApprove={ids => void handleBulkApprove(ids)}
            onBulkDecline={ids => void handleBulkDecline(ids)}
            selectedTickets={selectedTickets}
            onSelectAll={handleSelectAll}
            onSelectTicket={handleSelectTicket}
            className={styles.table}
            pageCount={usersList?.data.meta.totalPages ?? 0}
            handlePageChange={(selectedItem: { selected: number }) => {
              setPage(selectedItem.selected + 1);
            }}
          />
        </div>
      </Card>
    </>
  );
};
